import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import Card from "components/card/Card";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import TimePick from "components/timePick/TimePick";
import Input from "components/form/input";
import Checkbox from "components/form/checkbox/Checkbox";
import ButtonGroup from "components/buttonGroup/ButtonGroup";
import Callout from "components/callout/Callout";
import Divider from "components/divider";

import "./DiscountDateSelect.scss";

interface IDiscountDateSelectProps {
  updateState: (key: string, value: any) => void;
  timeState: ITimeState;
  show_dates: boolean;
}
export interface ITimeState {
  start_date?: Date;
  end_date?: Date;
  daily_start_time?: string;
  daily_end_time?: string;
  show_times?: boolean;
  show_dates?: boolean;
  interval?: "weeks";
  repeat_amount?: number;
  weekdays?: Array<number>;

  //interval 'week' selected
  //interval 'month' selected
  // monthly_rule_type?: "day_of_month" | "nth_weekday";
  // recurrence_type?: "does_not_repeat" | "daily" | "weekly" | "monthly" | "annually" | "every_week_day" | "custom";

  ends_type?: "" | "on" | "never";
  ends_after_occurrences?: string;
  auto_apply?: boolean;
  schedule_type?: "nth_weekday" | "date_range";
}

export default function DiscountDateSelect(props: IDiscountDateSelectProps) {
  const { updateState, timeState, show_dates } = props;
  const { t } = useTranslation();

  const [splitView, setSplitView] = useState<"date_range" | "nth_weekday">(
    timeState?.weekdays?.length ? "nth_weekday" : "date_range",
  );

  const initializedSplitView = useRef(false);
  useEffect(() => {
    if (initializedSplitView.current) {
      return;
    }

    setSplitView(timeState?.schedule_type);
    initializedSplitView.current = true;
  }, [timeState?.weekdays]);

  function handleDayOfWeekSelection(dayNumber: number) {
    let updatedSelection = timeState.weekdays ? [...timeState.weekdays] : [];
    if (updatedSelection.includes(dayNumber)) {
      updatedSelection = updatedSelection?.filter(num => num !== dayNumber);
    } else {
      updatedSelection.push(dayNumber);
    }
    updateState("weekdays", updatedSelection);
  }

  function handleSetView(key: "date_range" | "nth_weekday") {
    setSplitView(key);
    updateState("schedule_type", key);
  }

  const formatTime = (time: string) => {
    if (time == null) {
      return null;
    }
    const [hour, minute] = time.split(":");
    const h = Number(hour);
    return `${h % 12 || 12}:${minute} ${h >= 12 ? "PM" : "AM"}`;
  };

  const formatDate = (date: Date) => {
    return new Date(date).toDateString().split(" ").slice(1).join(" ");
  };

  const buildCallOutDescription = () => {
    const isDateRange = splitView === "date_range";

    const selectedAllDays = timeState?.weekdays?.length === 7;

    const days =
      isDateRange || selectedAllDays
        ? t("every day")
        : moment
            .weekdays()
            .filter((_, i) => timeState?.weekdays?.includes(i))
            .map(day => day.slice(0, 3))
            .join(", ");

    const repeat = !isDateRange
      ? Number(timeState.repeat_amount) === 1
        ? ` ${t("every week")}`
        : ` ${t("every")} ${timeState.repeat_amount} ${t("weeks")}`
      : "";

    const applies = isDateRange ? t("Applies every day") : `${t("Applies ")} ${days}${repeat}`;

    const startDate = timeState?.start_date ? formatDate(timeState.start_date) : null;

    const endDate = timeState?.ends_type !== "never" && timeState?.end_date ? formatDate(timeState.end_date) : null;

    const startTime = formatTime(timeState?.daily_start_time);
    const endTime = formatTime(timeState?.daily_end_time);

    // Date range
    if (isDateRange) {
      let result = applies;

      if (startDate && endDate) {
        result += t(` ${t("from")} ${startDate} ${t("to")} ${endDate}`);
      } else if (startDate) {
        result += t(` ${t("starting from")} ${startDate}`);
      } else if (endDate) {
        result += t(` ${t("ending on")} ${endDate}`);
      }

      if (startTime && endTime) {
        result += ` ${t("and from")} ${startTime} - ${endTime}`;
      } else if (startTime) {
        result += ` ${t("starting at")} ${startTime}`;
      } else if (endTime) {
        result += ` ${t("ending at")} ${endTime}`;
      }

      return result;
    }

    //ADD START DATE + END DATE + START TIME

    // Weekly
    if (startDate && endDate && startTime && endTime) {
      return `${applies} ${t("from")} ${startDate} - ${endDate}, ${startTime} - ${endTime}`;
    }

    if (startDate && endDate && startTime) {
      return `${applies} ${t("from")} ${startDate} - ${endDate} ${t("and starts at")} ${startTime}`;
    }

    if (startDate && endDate && endTime) {
      return `${applies} ${t("from")} ${startDate} - ${endDate} ${t("and ends at")} ${endTime}`;
    }

    if (startDate && startTime && endTime) {
      return `${applies} ${t("from")} ${startDate}, ${startTime} - ${endTime}`;
    }

    if (startDate && endDate) {
      return `${applies} ${t("from")} ${startDate} - ${endDate}`;
    }

    if (startDate && startTime) {
      return `${applies} ${t("starting from")} ${startDate} ${t("and starts at")} ${startTime}`;
    }

    if (startDate && endTime) {
      return `${applies} ${t("starting from")} ${startDate} ${t("and ends at")} ${endTime}`;
    }

    if (startDate) {
      return `${applies} ${t("starting from")} ${startDate}`;
    }

    return applies;
  };

  return (
    <>
      {show_dates ? (
        <Card
          title={t("Discount date(s) and time")}
          subtitle={t("Choose and define this discount's active duration and time")}
        >
          <div className="flex flex-col gap-4 p-6">
            {timeState?.auto_apply ? <Callout type="info" title={buildCallOutDescription()} /> : null}

            <ButtonGroup
              onButtonClick={(key: "date_range" | "nth_weekday") => handleSetView(key)}
              startKey={splitView}
              buttons={[
                {
                  label: t("Date Range"),
                  key: "date_range",
                },
                {
                  label: t("Day(s) of week"),
                  key: "nth_weekday",
                },
              ]}
            />
            {splitView === "date_range" ? (
              <>
                <DatePickerInput
                  months={1}
                  label={t("Discount Duration")}
                  position="left"
                  startingDate={timeState.start_date}
                  endingDate={timeState.end_date}
                  setStartingDate={(startingDate: Date) => updateState("start_date", startingDate)}
                  setEndingDate={(endingDate: Date) => updateState("end_date", endingDate)}
                />
              </>
            ) : (
              <>
                <DatePickerInput
                  months={1}
                  position="left"
                  label={t("Start date")}
                  startingDate={timeState.start_date}
                  setStartingDate={(startingDate: Date) => updateState("start_date", startingDate)}
                />

                <Input
                  label={t("Repeat every")}
                  type="number"
                  value={timeState.repeat_amount}
                  defaultValue={1}
                  onChange={repeat_amount => updateState("repeat_amount", repeat_amount.target.value)}
                  placeholder={"1"}
                  min={1}
                  suffix={t("Week(s)")}
                  // Save just in case we add in additional intervals
                  // trailingButtons={[
                  //   <SelectNew
                  //     className="rounded-none"
                  //     key={"interval"}
                  //     id={"interval"}
                  //     labelHidden
                  //     trailing
                  //     placeholder={t("Weeks")}
                  //     onChange={interval => updateState("interval", interval)}
                  //     value={timeState.interval}
                  //     options={[
                  //       { id: "weeks", label: t("Weeks") },
                  //       // { id: "days", label: t("Days") },
                  //       // { id: "months", label: t("Months") },
                  //       // { id: "years", label: t("Years") },
                  //     ]}
                  //     usePortal
                  //   />,
                  // ]}
                />

                {timeState?.interval === "weeks" ? (
                  <>
                    <div className={"flex items-center gap-4 flex-wrap"}>
                      {/* Sunday = 0 ...  Saturday = 6 */}
                      {moment?.weekdays()?.map((weekDay, index) => {
                        return (
                          <div key={index} className="grow-0">
                            <Checkbox
                              size="small"
                              selectionOutline
                              checked={timeState?.weekdays?.includes(index)}
                              label={weekDay?.slice(0, 3)}
                              onChange={() => handleDayOfWeekSelection(index)}
                            />
                          </div>
                        );
                      })}
                    </div>

                    {timeState?.weekdays?.length === 0 ? (
                      <Callout type="error" description={t("Please select at least one day.")} title="" />
                    ) : null}
                  </>
                ) : null}

                {/* End Type - Never End or End On Date */}
                <div>
                  <p className="ui-events-calendar-label">{t("Ends")}</p>
                  <div className="flex w-full items-center gap-3">
                    <div className="shrink-0">
                      <Checkbox
                        size="small"
                        circle
                        label={t("Never")}
                        checked={timeState?.ends_type === "never"}
                        onChange={() => updateState("ends_type", "never")}
                      />
                    </div>

                    <div className="shrink-0">
                      <Checkbox
                        size="small"
                        circle
                        label={t("On")}
                        checked={timeState?.ends_type === "on"}
                        onChange={() => updateState("ends_type", "on")}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <DatePickerInput
                        months={1}
                        position="left"
                        disabled={timeState?.ends_type !== "on"}
                        startingDate={timeState.end_date}
                        setStartingDate={(endingDate: Date) => updateState("end_date", endingDate)}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            <div>
              <>
                <div className="-mt-2 -mb-2">
                  <Divider />
                </div>
              </>
              <div className="flex w-full items-start gap-4">
                <TimePick
                  value={timeState.daily_start_time}
                  onChange={daily_start_time => updateState("daily_start_time", daily_start_time)}
                  label={t("Discount Start Time")}
                  size="large"
                />

                <TimePick
                  value={timeState.daily_end_time}
                  onChange={daily_end_time => updateState("daily_end_time", daily_end_time)}
                  label={t("Discount End Time")}
                  size="large"
                />
              </div>
              <div className="-m-4 pt-2">
                <Callout
                  type="info"
                  borderless
                  title=""
                  description={t("Leave the start and end times blank for no time restrictions.")}
                />
              </div>
            </div>
          </div>
        </Card>
      ) : null}
    </>
  );
}
